import { Save, UndoOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Theme,
  useMediaQuery
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import {
  NeighborhoodDropdown,
  Page,
  PageTitleWithBack,
  Table,
  TerritoryDropdown
} from '../../../components';
import { getDelayedJobs, updateDelayedJobs } from '../../../fetch';
import { MobileDelayStarts } from './MobileDelayStarts';
import { DatePicker } from '@mui/x-date-pickers';

export const DelayStarts = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedStartWeek, setSelectedStartWeek] = useState<Date | null>(null);
  const [weekNumber, setWeekNumber] = useState<number | null>(null);
  const [weeksToDelay, setWeeksToDelay] = useState<string | number>('');
  const [isGettingPreview, setGettingPreview] = useState(false);
  const [delayedJobsLoading, setDelayedJobsLoading] = useState(true);
  const [delayedJobs, setDelayedJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const isMobile = useMediaQuery('(max-width: 1400px)');

  const fetchDelayedJobs = async (week = null, delay = null) => {
    setDelayedJobsLoading(true);
    try {
      const res = await getDelayedJobs({
        territoryId: selectedTerritoryId,
        neighborhoodId: selectedNeighborhoodId,
        startWeekToDelayFrom: week,
        weeksToDelay: delay
      });

      if (res.length === 0) {
        enqueueSnackbar('No active jobs fit the search criteria', {
          variant: 'warning'
        });
      }
      setDelayedJobs(res);
      setSelectedJobs(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ? `Error, ${error?.Detail}` : `Error fetching jobs.`, {
        variant: 'error'
      });
    } finally {
      setDelayedJobsLoading(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({ cell }) => {
          const job = cell.row.original;
          const ids = selectedJobs.map(selectedJob => selectedJob.jobId);
          const isSelected = ids.includes(job.jobId);
          const showCheckbox = true;
          return (
            showCheckbox && (
              <FormControlLabel
                classes={{
                  label: classes.tableCheckbox
                }}
                color="primary"
                control={
                  <Checkbox
                    checked={isSelected}
                    id="roles-checkbox"
                    onChange={() => {
                      isSelected
                        ? setSelectedJobs(
                            selectedJobs.filter(selectedJob => {
                              return selectedJob.jobId !== job.jobId;
                            })
                          )
                        : setSelectedJobs([...selectedJobs, job]);
                    }}
                  />
                }
                label=""
              />
            )
          );
        }
      },
      {
        Header: 'Job',
        accessor: 'jobId',
        sort: false
      },
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        sort: false
      },
      {
        Header: 'Phase',
        accessor: 'neighborhoodPhase',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false
      },
      {
        Header: 'PM',
        accessor: 'projectManager',
        sort: false
      },
      {
        Header: 'Super',
        accessor: 'supervisor',
        sort: false
      },
      {
        Header: 'Start Week',
        accessor: 'startWeek',
        sort: false
      },
      {
        Header: 'Form Date',
        accessor: 'formDateDisplay',
        sort: false
      },
      {
        Header: 'Frame Date',
        accessor: 'frameDateDisplay',
        sort: false
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedJobs, selectedJobs]);

  return (
    <>
      <Page title="Delay Starts">
        <PageTitleWithBack backText="Back to Admin home" backUrl="/admin" title="Delay Starts" />
        <Grid container alignItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6}>
            <TerritoryDropdown
              value={selectedTerritoryId}
              handleChange={val => {
                setSelectedTerritoryId(val);
              }}
              handleClear={() => {
                setSelectedTerritoryId('');
                setSelectedNeighborhoodId('');
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NeighborhoodDropdown
              value={selectedNeighborhoodId}
              handleChange={value => {
                setSelectedNeighborhoodId(value);
              }}
              isDisabled={!selectedTerritoryId}
              apiParams={{ territoryId: selectedTerritoryId }}
              isAdminPortal={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <DatePicker
                label={!!weekNumber ? `Week ${weekNumber}` : `Start Week to Delay From`}
                value={selectedStartWeek}
                onChange={(selectedDate: Date) => {
                  if (selectedDate === null) {
                    setWeekNumber(null);
                    return;
                  }
                  //https://www.javatpoint.com/calculate-current-week-number-in-javascript
                  //Calculate the week number given a date. Still displaying date currently
                  const oneJan = new Date(selectedDate.getFullYear(), 0, 1);
                  const numberOfDays = Math.floor(
                    (selectedDate.valueOf() - oneJan.valueOf()) / (24 * 60 * 60 * 1000)
                  );
                  const numberOfWeek = Math.ceil((selectedDate.getDay() + 1 + numberOfDays) / 7);

                  setWeekNumber(numberOfWeek);
                  setSelectedStartWeek(selectedDate);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    required: delayedJobs.length > 0
                  }
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required={delayedJobs.length > 0}
              fullWidth
              size="small"
              autoComplete="nope"
              label="Weeks to Delay"
              name="weeksDelay"
              value={weeksToDelay}
              onChange={e => {
                const re = /^[0-9]+$/;
                const value = e.target.value;
                // make sure it is number only before we update state
                if (value === '' || value === null || re.test(value)) {
                  setWeeksToDelay(value);
                }
              }}
            />
          </Grid>
        </Grid>
        <Grid container justifyContent="center" alignItems="center" className={classes.wrapper}>
          <Divider />
        </Grid>

        {delayedJobs.length > 0 && !delayedJobsLoading && selectedJobs && (
          <Grid container>
            <Grid item xs={12}>
              <Table
                columns={columns}
                data={delayedJobs}
                isLoading={delayedJobsLoading}
                hidePagination
                mobileBreakPoint={1400}
                ResponsiveComponent={isMobile ? MobileDelayStarts : undefined}
                mobileProps={{
                  setSelectedJobs: setSelectedJobs,
                  selectedJobs: selectedJobs
                }}
              />
            </Grid>
          </Grid>
        )}
        <Grid container alignItems="center" className={classes.saveWrapper}>
          <ButtonGroup>
            <Button
              color="inherit"
              variant="contained"
              disabled={false}
              startIcon={<UndoOutlined />}
              onClick={() => {
                setSelectedTerritoryId('');
                setSelectedNeighborhoodId('');
                setSelectedStartWeek(null);
                setWeeksToDelay('');
                setWeekNumber(null);
                setDelayedJobs([]);
              }}
            >
              Restart
            </Button>
            <LoadingButton
              color="primary"
              variant="contained"
              loading={saving || isGettingPreview}
              disabled={delayedJobs.length > 0 && (!weeksToDelay || !weekNumber)}
              loadingPosition="start"
              startIcon={<Save />}
              onClick={async () => {
                if (delayedJobs.length > 0) {
                  setSaving(true);
                  try {
                    await updateDelayedJobs({
                      jobIds: selectedJobs.map(job => job.jobId),
                      startWeekToDelayFrom: weekNumber,
                      weeksToDelay: Number(weeksToDelay)
                    });
                    enqueueSnackbar('Jobs successfully delayed', {
                      variant: 'success'
                    });
                    setSelectedTerritoryId('');
                    setSelectedNeighborhoodId('');
                    setSelectedStartWeek(null);
                    setWeeksToDelay('');
                    setWeekNumber(null);
                    setDelayedJobs([]);
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ? `Error, ${error?.Detail}` : `Error saving.`, {
                      variant: 'error'
                    });
                  } finally {
                    setSaving(false);
                  }
                } else {
                  setGettingPreview(true);
                  await fetchDelayedJobs(weekNumber, weeksToDelay);
                  setGettingPreview(false);
                }
              }}
            >
              {delayedJobs.length > 0 ? 'Save Changes' : 'Preview Changes'}
            </LoadingButton>
          </ButtonGroup>
        </Grid>
      </Page>
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  tableCheckbox: {
    marginRight: 0
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  }
}));
